"use client";

import { useState, useCallback, useRef, useMemo, memo } from "react";
import {
  SmilePlus,
  Reply,
  Pencil,
  Trash2,
  Check,
  AlertCircle,
  RefreshCw,
  MessageSquare,
} from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { Button } from "ui";
import type { OptimisticMessage } from "@/lib/types/channel";
import type { MessagesAction } from "../../store/messages-reducer";
import { trpc } from "@/lib/trpc/client";
import { getInitials } from "@/lib/utils";
import { EditorRenderer } from "editor";
import MessageReactions from "./message-reactions";
import InlineEditor from "./inline-editor";
import MemberProfilePopover from "../member-profile-popover";

const COMMON_EMOJIS = ["👍", "❤️", "😂", "🔥", "🎉", "👀"];

/**
 * Which backend router owns these messages. Channel messages live in
 * `StudioChannelMessage` (messages.* procedures); DMs live in
 * `StudioConversationMessage` (conversations.* procedures). Using the wrong
 * router for a given id throws NOT_FOUND, so the surface must be explicit.
 */
export type MessageSurface = "channel" | "conversation";

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (isToday(date)) return format(date, "HH:mm");
  if (isYesterday(date)) return `Yesterday ${format(date, "HH:mm")}`;
  return format(date, "MMM d, HH:mm");
}

function formatFullTimestamp(iso: string): string {
  const date = new Date(iso);
  return format(date, "EEEE, MMMM d, yyyy 'at' HH:mm:ss");
}

type Props = {
  message: OptimisticMessage;
  currentMemberId: string;
  isAdmin: boolean;
  dispatch: React.Dispatch<MessagesAction>;
  onReplyClick: (message: OptimisticMessage) => void;
  onRetry: (message: OptimisticMessage) => void;
  isContinuation?: boolean;
  reactionsDisabled?: boolean;
  replyDisabled?: boolean;
  surface?: MessageSurface;
};

function MessageItem({
  message,
  currentMemberId,
  isAdmin,
  dispatch,
  onReplyClick,
  onRetry,
  isContinuation = false,
  reactionsDisabled = false,
  replyDisabled = false,
  surface = "channel",
}: Props) {
  const client = trpc.useUtils().client;

  // Route edit/delete/react to the router that owns this message's table.
  const messageApi = useMemo(
    () =>
      surface === "conversation"
        ? {
            delete: (messageId: string) =>
              client.conversations.deleteMessage.mutate({ messageId }),
            edit: (messageId: string, content: string) =>
              client.conversations.editMessage.mutate({ messageId, content }),
            react: (messageId: string, emoji: string) =>
              client.conversations.react.mutate({ messageId, emoji }),
          }
        : {
            delete: (messageId: string) =>
              client.messages.delete.mutate({ messageId }),
            edit: (messageId: string, content: string) =>
              client.messages.edit.mutate({ messageId, content }),
            react: (messageId: string, emoji: string) =>
              client.messages.react.mutate({ messageId, emoji }),
          },
    [surface, client],
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const confirmDeleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const isAuthor = message.member.id === currentMemberId;
  const isDeleted = message.deletedAt !== null;
  const isEdited = message.editedAt !== null;
  const canEdit = isAuthor && !isDeleted;
  // Conversation (DM) messages are author-only server-side; admins have no
  // special delete rights there, so only grant admin-delete on channels.
  const canDelete =
    (isAuthor || (isAdmin && surface === "channel")) && !isDeleted;

  const displayName = message.member.profile?.displayName ?? "Unknown Member";

  const handleReact = useCallback(
    async (emoji: string) => {
      setShowEmojiPicker(false);
      const existing = message.reactions.find(
        (r) => r.emoji === emoji && r.memberId === currentMemberId,
      );

      // Optimistic toggle
      dispatch({
        type: "TOGGLE_REACTION",
        messageId: message.id,
        reaction: {
          id: `temp-${Date.now()}`,
          emoji,
          memberId: currentMemberId,
        },
        action: existing ? "removed" : "added",
      });

      try {
        await messageApi.react(message.id, emoji);
      } catch {
        // Revert on failure
        dispatch({
          type: "TOGGLE_REACTION",
          messageId: message.id,
          reaction: {
            id: `temp-${Date.now()}`,
            emoji,
            memberId: currentMemberId,
          },
          action: existing ? "added" : "removed",
        });
      }
    },
    [message, currentMemberId, dispatch, messageApi],
  );

  const handleDelete = useCallback(async () => {
    dispatch({ type: "SOFT_DELETE", messageId: message.id });
    try {
      await messageApi.delete(message.id);
    } catch {
      dispatch({
        type: "PATCH",
        messageId: message.id,
        patch: { deletedAt: null, content: message.content },
      });
    }
  }, [message, dispatch, messageApi]);

  const handleEditSave = useCallback(
    async (newContent: string) => {
      setIsEditing(false);
      const prev = message.content;
      dispatch({
        type: "PATCH",
        messageId: message.id,
        patch: { content: newContent, editedAt: new Date().toISOString() },
      });
      try {
        await messageApi.edit(message.id, newContent);
      } catch {
        dispatch({
          type: "PATCH",
          messageId: message.id,
          patch: { content: prev, editedAt: message.editedAt },
        });
      }
    },
    [message, dispatch, messageApi],
  );

  const handleDeleteClick = useCallback(() => {
    if (confirmingDelete) {
      if (confirmDeleteTimerRef.current) {
        clearTimeout(confirmDeleteTimerRef.current);
        confirmDeleteTimerRef.current = null;
      }
      setConfirmingDelete(false);
      handleDelete();
    } else {
      setConfirmingDelete(true);
      confirmDeleteTimerRef.current = setTimeout(() => {
        setConfirmingDelete(false);
        confirmDeleteTimerRef.current = null;
      }, 3000);
    }
  }, [confirmingDelete, handleDelete]);

  const initials = getInitials(displayName);

  if (isDeleted) {
    return null;
  }

  const isSending = message.optimisticStatus === "sending";
  const isFailed = message.optimisticStatus === "failed";

  return (
    <div
      className={`group relative flex items-start gap-2 px-5 transition-colors ${
        isContinuation ? "py-0.5" : "pb-1 pt-2"
      } ${isHovered ? "bg-accent/50" : ""} ${isSending ? "opacity-70" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowEmojiPicker(false);
        setConfirmingDelete(false);
        if (confirmDeleteTimerRef.current) {
          clearTimeout(confirmDeleteTimerRef.current);
          confirmDeleteTimerRef.current = null;
        }
      }}
    >
      {isContinuation ? (
        <div className="flex w-9 flex-shrink-0 items-start justify-end">
          <span
            className={`text-xs leading-snug text-muted-foreground ${isHovered ? "opacity-100" : "opacity-0"}`}
          >
            {formatTimestamp(message.createdAt)}
          </span>
        </div>
      ) : (
        <MemberProfilePopover member={message.member}>
          <div className="mt-0.5 h-9 w-9 flex-shrink-0 cursor-pointer overflow-hidden rounded-md bg-muted">
            {message.member.profile?.image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={
                  message.member.profile.image.startsWith("http")
                    ? message.member.profile.image
                    : `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/${message.member.profile.image}`
                }
                alt={displayName}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-xs font-semibold text-muted-foreground">
                {initials}
              </span>
            )}
          </div>
        </MemberProfilePopover>
      )}

      <div className="min-w-0 flex-1">
        {!isContinuation && (
          <div className="flex items-baseline gap-2">
            <MemberProfilePopover member={message.member}>
              <span className="cursor-pointer text-sm font-bold leading-snug text-foreground hover:underline">
                {displayName}
              </span>
            </MemberProfilePopover>
            <span
              className="cursor-default text-xs leading-snug text-grunge40 hover:underline dark:text-grunge60"
              title={formatFullTimestamp(message.createdAt)}
            >
              {formatTimestamp(message.createdAt)}
            </span>
            {isEdited && (
              <span className="text-xs leading-snug text-muted-foreground">
                (edited)
              </span>
            )}
            {isSending && (
              <span className="text-xs leading-snug text-muted-foreground">
                Sending...
              </span>
            )}
          </div>
        )}
        {isContinuation && isEdited && (
          <span className="text-xs text-muted-foreground">(edited)</span>
        )}

        {isEditing ? (
          <InlineEditor
            initialContent={message.content}
            onSave={handleEditSave}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <EditorRenderer
            compact
            content={message.content}
            className="message-content text-sm leading-snug text-foreground/90"
          />
        )}

        {isFailed && (
          <div className="mt-1 flex items-center gap-1 text-xs text-red-500">
            <AlertCircle className="h-3 w-3" />
            Failed to send.
            <Button
              variant="link"
              size="sm"
              onClick={() => onRetry(message)}
              icon={<RefreshCw />}
              className="text-red-500 underline"
            >
              Retry
            </Button>
          </div>
        )}

        {message.reactions.length > 0 && (
          <MessageReactions
            reactions={message.reactions}
            currentMemberId={currentMemberId}
            onToggle={handleReact}
            disabled={reactionsDisabled}
          />
        )}

        {message._count.replies > 0 && !message.parentId && (
          <button
            onClick={() => onReplyClick(message)}
            className="group/thread -ml-1 mt-1 flex items-center gap-1.5 rounded px-1 py-0.5 text-xs transition-colors hover:bg-accent/50"
          >
            <MessageSquare className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" />
            <span className="font-semibold text-indigo-500 dark:text-indigo-400">
              {message._count.replies}{" "}
              {message._count.replies === 1 ? "reply" : "replies"}
            </span>
            <span className="text-grunge40 opacity-0 transition-opacity group-hover/thread:opacity-100 dark:text-grunge60">
              View thread
            </span>
          </button>
        )}
      </div>

      {!isSending && !isEditing && (
        <div
          className={`absolute right-5 top-0 z-10 flex -translate-y-1/2 items-center gap-1 rounded-md border border-border bg-popover px-1 py-0.5 shadow-sm transition-opacity duration-100 ${
            isHovered || showEmojiPicker || confirmingDelete
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0"
          }`}
        >
          {!reactionsDisabled && (
            <div className="relative flex items-center">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setShowEmojiPicker((p) => !p)}
                icon={<SmilePlus />}
                title="React with an emoji"
              />
              {showEmojiPicker && (
                <div className="absolute right-0 top-full z-10 mt-1 flex gap-1 rounded-md border border-border bg-popover p-1.5 shadow-lg">
                  {COMMON_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleReact(emoji)}
                      className="rounded p-1 text-base leading-none hover:bg-accent"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!message.parentId && !replyDisabled && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onReplyClick(message)}
              icon={<Reply />}
              title="Reply in thread"
            />
          )}

          {canEdit && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setIsEditing(true)}
              icon={<Pencil />}
              title="Edit this message"
            />
          )}

          {canDelete && (
            <Button
              variant={confirmingDelete ? "destructive-subtle" : "ghost"}
              size="icon-sm"
              onClick={handleDeleteClick}
              icon={confirmingDelete ? <Check /> : <Trash2 />}
              title={
                confirmingDelete
                  ? "Click again to confirm delete"
                  : "Delete this message"
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

export default memo(MessageItem);
